import { Eye, History } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type {
  AgentFallbackBehavior,
  AgentResponseTone,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { AgentDraft } from "@/features/agents/components/AgentDraft";
import { PromptHistoryDialog } from "@/features/agents/components/PromptHistoryDialog";
import { PromptPreviewDialog } from "@/features/agents/components/PromptPreviewDialog";
import { useToneTemplates } from "@/features/prompt-tones/hooks/useToneTemplates";

interface Props {
  draft: AgentDraft;
  onChange: (next: AgentDraft) => void;
  agentId?: string;
}

const TONES: AgentResponseTone[] = [
  "formal_technical",
  "direct",
  "didactic",
  "casual",
];
// "escalate_human" removido da UI -- nao havera fluxo de escalonamento.
// O campo continua no enum do backend para compat.
const FALLBACKS: AgentFallbackBehavior[] = [
  "inform_unknown",
  "try_with_context",
];

export function BehaviorTab({ draft, onChange, agentId }: Props) {
  const { t } = useTranslation("agents");
  const tonesQuery = useToneTemplates();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <Label htmlFor="agent-system-prompt">
            {t("behavior.systemPrompt")} *
          </Label>
          {agentId ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setHistoryOpen(true)}
            >
              <History className="h-3.5 w-3.5" />
              {t("behavior.history")}
            </Button>
          ) : null}
        </div>
        <Textarea
          id="agent-system-prompt"
          value={draft.system_prompt}
          onChange={(e) =>
            onChange({ ...draft, system_prompt: e.target.value })
          }
          rows={10}
          className="font-mono text-sm"
          placeholder={t("behavior.systemPromptPlaceholder")}
        />
      </div>

      {agentId ? (
        <PromptHistoryDialog
          agentId={agentId}
          currentPrompt={draft.system_prompt}
          open={historyOpen}
          onOpenChange={setHistoryOpen}
        />
      ) : null}

      <div className="grid gap-4 md:grid-cols-3">
        <div className="space-y-2">
          <Label>{t("behavior.responseTone")}</Label>
          <Select
            value={draft.response_tone ?? "none"}
            onValueChange={(v) =>
              onChange({
                ...draft,
                response_tone: v === "none" ? null : (v as AgentResponseTone),
              })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder={t("behavior.responseTonePlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("behavior.responseToneNone")}</SelectItem>
              {TONES.map((tone) => (
                <SelectItem key={tone} value={tone}>
                  {tonesQuery.data?.find((tpl) => tpl.key === tone)?.name ??
                    t(`behavior.tones.${tone}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="agent-language">
            {t("behavior.responseLanguage")}
          </Label>
          <Input
            id="agent-language"
            value={draft.response_language}
            onChange={(e) =>
              onChange({ ...draft, response_language: e.target.value })
            }
            placeholder={t("behavior.responseLanguagePlaceholder")}
          />
        </div>
        <div className="space-y-2">
          <Label>{t("behavior.fallbackBehavior")}</Label>
          <Select
            value={draft.fallback_behavior ?? "none"}
            onValueChange={(v) =>
              onChange({
                ...draft,
                fallback_behavior:
                  v === "none" ? null : (v as AgentFallbackBehavior),
              })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder={t("behavior.fallbackBehaviorPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("behavior.fallbackBehaviorDefault")}</SelectItem>
              {FALLBACKS.map((fb) => (
                <SelectItem key={fb} value={fb}>
                  {t(`behavior.fallbacks.${fb}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-3 rounded-md border bg-muted/30 p-3">
        <div className="flex items-center gap-2">
          <input
            id="agent-cite-sources"
            type="checkbox"
            checked={draft.cite_sources}
            onChange={(e) =>
              onChange({ ...draft, cite_sources: e.target.checked })
            }
          />
          <Label htmlFor="agent-cite-sources">{t("behavior.citeSources")}</Label>
        </div>
        <div className="flex items-center gap-2">
          <input
            id="agent-memory"
            type="checkbox"
            checked={draft.memory_enabled}
            onChange={(e) =>
              onChange({ ...draft, memory_enabled: e.target.checked })
            }
          />
          <Label htmlFor="agent-memory">{t("behavior.memoryEnabled")}</Label>
        </div>
      </div>

      {agentId ? (
        <div>
          <Button
            type="button"
            variant="outline"
            onClick={() => setPreviewOpen(true)}
          >
            <Eye className="h-4 w-4" />
            {t("actions.preview")}
          </Button>
          <PromptPreviewDialog
            open={previewOpen}
            onOpenChange={setPreviewOpen}
            agentId={agentId}
            draft={draft}
          />
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          {t("behavior.previewHint")}
        </p>
      )}
    </div>
  );
}
